import { useRef, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, Calendar, FlaskConical, CheckCircle, Users, BarChart3, Globe, Bell, Video, MessageSquare, Lock, FileText } from "lucide-react";

function useReveal() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return { ref, visible };
}

const platforms = [
  {
    id: "levelup", name: "LevelUp", tag: "SELF-PACED LEARNING",
    headline: "Self-Paced Microsoft Learning for Partners",
    subline: "Microsoft technology, on-demand training built exclusively for the Microsoft Partner Network",
    color: "#07641D", gradient: "from-[#194449] to-[#091F2C]", icon: BookOpen,
    logo: "/levelup-logo-light.svg",
    logoStyle: { height: 36, width: "auto", filter: "brightness(0) invert(1)" },
    href: "https://skillupwithlevelup.com/", external: true,
    overview: "LevelUp is Technofocus's flagship self-paced learning platform, built in partnership with Microsoft to deliver on-demand training for Microsoft Partners. Covering all FY26 Microsoft solution areas, LevelUp provides Partners with the knowledge they need to build sales and technical expertise — and help customers adopt Microsoft cloud technologies.",
    features: [
      { icon: BookOpen, title: "800+ Microsoft Courses", desc: "Comprehensive catalog covering all FY26 solution areas" },
      { icon: Globe, title: "Global Availability", desc: "Access from anywhere, 24/7, in your preferred language" },
      { icon: BarChart3, title: "Progress Tracking", desc: "Monitor completion rates, scores, and certification readiness" },
      { icon: Users, title: "Partner-Exclusive Content", desc: "Built for designed for Partner needs" },
    ],
    highlights: ["Microsoft technology platform", "All 6 FY26 solution areas covered", "Certification preparation content", "Self-paced, on-demand delivery", "Progress tracking & reporting", "Global learner access 24/7"],
    image: "https://images.unsplash.com/photo-1758691736548-073bc97cdff4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwyfHxjb3Jwb3JhdGUlMjBJVCUyMHRyYWluaW5nJTIwcHJvZmVzc2lvbmFsc3xlbnwwfHx8fDE3NzUwMTkzODZ8MA&ixlib=rb-4.1.0&q=85",
  },
  {
    id: "eventsup", name: "EventsUp", tag: "LIVE EVENTS & TRAINING",
    headline: "The Complete Event Management Suite",
    subline: "Powered by Cvent — for live IT training, virtual sessions, and large-scale Microsoft events",
    color: "#0B2E38", gradient: "from-[#091F2C] to-[#194449]", icon: Calendar,
    logo: "/eventsup-logo-light.svg",
    logoStyle: { height: 40, width: "auto" },
    poweredBy: { logo: "/cvent-logo.png", label: "Powered by Cvent" },
    href: "https://technofocus.ai/eventsup", external: true,
    overview: "EventsUp is our fully managed, end-to-end event platform built on Cvent. Whether you're hosting a live IT training, virtual conference, or Microsoft technology event — EventsUp handles every detail, from registration to post-event reporting.",
    features: [
      { icon: Globe, title: "Custom Event Websites", desc: "Branded registration pages tailored to each event" },
      { icon: Bell, title: "Automated Communications", desc: "Pre and post-event emails, reminders and notifications" },
      { icon: Video, title: "Virtual & Live Streaming", desc: "Live, collaborative, and pre-recorded session formats" },
      { icon: BarChart3, title: "Real-Time Reporting", desc: "Live attendance stats, email deliverability, and analytics" },
    ],
    highlights: ["Powered by Cvent enterprise platform", "Custom event website creation", "End-to-end attendee registration", "Automated email communication", "Virtual event & live streaming", "Stakeholder reporting portal with MFA"],
    image: "https://images.pexels.com/photos/7793750/pexels-photo-7793750.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    id: "stepup", name: "StepUp", tag: "SIMULATED LABS",
    headline: "Project-Ready Technical Skilling & Lab Experience",
    subline: "Immersive, hands-on lab environments for the latest Microsoft courses",
    color: "#07641D", gradient: "from-[#07641D] to-[#194449]", icon: FlaskConical,
    logo: "/stepup-logo-new.svg",
    logoStyle: { height: 40, width: "auto" },
    href: "https://stepup.technofocus.ai/", external: true,
    overview: "StepUp is our simulated lab platform, providing learners with real-world, hands-on practice in live Microsoft environments. Delivering Microsoft technology StepUp goes beyond theory — giving practitioners the actual experience of configuring, deploying, and managing Microsoft technologies in realistic scenarios.",
    features: [
      { icon: FlaskConical, title: "Real-World Lab Scenarios", desc: "Practice actual Microsoft deployment and config tasks" },
      { icon: Lock, title: "Secure Lab Environments", desc: "Isolated, sandboxed environments for safe experimentation" },
      { icon: MessageSquare, title: "Guided Instructions", desc: "Step-by-step guidance for each lab scenario" },
      { icon: FileText, title: "Progress Assessment", desc: "Track lab completion and measure practical competency" },
    ],
    highlights: ["Microsoft technology lab content", "Latest Azure & M365 scenarios", "Isolated, sandboxed environments", "Guided step-by-step lab instructions", "Project-ready skill development", "Regular updates with new technologies"],
    image: "https://images.pexels.com/photos/7693114/pexels-photo-7693114.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
];

export default function Platforms() {
  const [activeTab, setActiveTab] = useState("levelup");
  const activePlatform = platforms.find(p => p.id === activeTab);

  return (
    <div className="overflow-x-hidden" style={{ backgroundColor: '#FFF8F3' }}>
      {/* ── HERO ── */}
      <section data-testid="platforms-hero-section" className="pt-32 pb-20"
        style={{ background: "linear-gradient(135deg, #091F2C 0%, #194449 55%, #1E5C60 100%)" }}>
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <p className="text-xs font-bold uppercase tracking-[0.2em] mb-5" style={{ color: '#D4EC8E' }}>Our Platform Ecosystem</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light text-white tracking-tight mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Three Platforms.<br />
            <span style={{ color: '#D4EC8E', fontWeight: 600 }}>One Mission.</span>
          </h1>
          <p className="text-lg max-w-2xl mx-auto" style={{ color: 'rgba(255,255,255,0.7)' }}>
            From self-paced learning to live events to hands-on labs — a complete Microsoft training ecosystem for Partners, enterprises, and technology professionals.
          </p>
        </div>
      </section>

      {/* ── TABS ── */}
      <section data-testid="platform-tabs-section" className="sticky top-16 z-40 bg-white border-b shadow-sm" style={{ borderColor: '#E2EBE9' }}>
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
            {platforms.map((p) => (
              <button key={p.id} data-testid={`platform-tab-${p.id}`}
                onClick={() => setActiveTab(p.id)}
                className={`flex items-center gap-2.5 px-7 py-4 whitespace-nowrap text-sm font-semibold border-b-2 transition-all`}
                style={{
                  borderBottomColor: activeTab === p.id ? p.color : 'transparent',
                  color: activeTab === p.id ? p.color : '#9CA3AF',
                }}>
                <p.icon size={17} />
                {p.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ── PLATFORM CONTENT ── */}
      {activePlatform && (
        <section key={activeTab} data-testid={`platform-content-${activeTab}`} className="py-20 md:py-28 bg-white animate-fade-in">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            {/* Hero strip */}
            <div className={`rounded-3xl bg-gradient-to-br ${activePlatform.gradient} p-10 md:p-14 mb-14 text-white`}>
              <div className="flex flex-col md:flex-row gap-8 items-start">
                <div className="flex-1">
                  <span className="text-white/60 text-xs font-bold uppercase tracking-wider mb-3 block">{activePlatform.tag}</span>
                  {/* Platform Logo */}
                  {activePlatform.logo ? (
                    <div className="mb-4">
                      <img
                        src={activePlatform.logo}
                        alt={activePlatform.name}
                        style={activePlatform.logoStyle}
                        className="max-w-[220px]"
                      />
                      {activePlatform.poweredBy && (
                        <div className="flex items-center gap-2 mt-3">
                          <span className="text-white/50 text-xs">Powered by</span>
                          <img src={activePlatform.poweredBy.logo} alt="Cvent" style={{ height: 18, width: "auto", filter: "brightness(0) invert(1)", opacity: 0.7 }} />
                        </div>
                      )}
                    </div>
                  ) : (
                    <h2 className="text-3xl md:text-4xl font-semibold mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>{activePlatform.headline}</h2>
                  )}
                  <h2 className="text-2xl md:text-3xl font-semibold mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>{activePlatform.headline}</h2>
                  <p className="text-white/80 text-base mb-6">{activePlatform.subline}</p>
                  <a href={activePlatform.href} target="_blank" rel="noopener noreferrer"
                    data-testid={`platform-hero-cta-${activePlatform.id}`}
                    className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold transition-colors"
                    style={{ backgroundColor: '#D4EC8E', color: '#091F2C' }}
                    onMouseEnter={e => e.currentTarget.style.backgroundColor = '#BFD872'}
                    onMouseLeave={e => e.currentTarget.style.backgroundColor = '#D4EC8E'}>
                    Visit {activePlatform.name} <ArrowRight size={16} />
                  </a>
                </div>
                <div className="md:w-2/5">
                  <img src={activePlatform.image} alt={activePlatform.name}
                    className="rounded-2xl w-full object-cover shadow-xl" style={{ height: 240 }} />
                </div>
              </div>
            </div>

            {/* Overview + features */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
              <div className="lg:col-span-2">
                <h3 className="text-2xl font-semibold text-gray-900 mb-5" style={{ fontFamily: 'Outfit, sans-serif' }}>Platform Overview</h3>
                <p className="text-gray-600 leading-relaxed text-base mb-8">{activePlatform.overview}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {activePlatform.features.map((feature) => (
                    <div key={feature.title} className="p-5 rounded-2xl border" style={{ backgroundColor: '#FFF8F3', borderColor: '#E2EBE9' }}>
                      <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center mb-3 shadow-sm">
                        <feature.icon size={18} style={{ color: activePlatform.color }} />
                      </div>
                      <h4 className="font-semibold text-gray-900 mb-1 text-sm" style={{ fontFamily: 'Outfit, sans-serif' }}>{feature.title}</h4>
                      <p className="text-gray-500 text-sm">{feature.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
              {/* Highlights */}
              <div>
                <div className="rounded-2xl p-7 border" style={{ backgroundColor: '#FFF8F3', borderColor: '#E2EBE9' }}>
                  <h4 className="font-semibold text-gray-900 mb-5" style={{ fontFamily: 'Outfit, sans-serif' }}>Platform Highlights</h4>
                  <ul className="space-y-3">
                    {activePlatform.highlights.map((h) => (
                      <li key={h} className="flex items-start gap-2.5 text-sm text-gray-700">
                        <CheckCircle size={15} className="shrink-0 mt-0.5" style={{ color: activePlatform.color }} />
                        {h}
                      </li>
                    ))}
                  </ul>
                  <a href={activePlatform.href} target="_blank" rel="noopener noreferrer"
                    data-testid={`platform-highlights-cta-${activePlatform.id}`}
                    className="mt-7 w-full flex items-center justify-center gap-2 text-white py-3.5 rounded-full font-semibold transition-colors"
                    style={{ backgroundColor: activePlatform.color }}
                    onMouseEnter={e => e.currentTarget.style.opacity = '0.88'}
                    onMouseLeave={e => e.currentTarget.style.opacity = '1'}>
                    Get Started <ArrowRight size={14} />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ── ALL PLATFORMS ── */}
      <section data-testid="all-platforms-overview" className="py-16" style={{ backgroundColor: '#FFF8F3' }}>
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {platforms.map((p) => (
              <div key={p.id} data-testid={`platform-overview-card-${p.id}`}
                className={`rounded-2xl p-6 text-white cursor-pointer bg-gradient-to-br ${p.gradient} hover:shadow-xl hover:-translate-y-1 transition-all duration-300 ${activeTab === p.id ? 'ring-2 ring-white/30 ring-offset-2' : ''}`}
                onClick={() => { setActiveTab(p.id); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
                <p.icon size={22} className="mb-4 text-white" />
                <h3 className="font-bold text-lg mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{p.name}</h3>
                <p className="text-white/60 text-xs font-bold uppercase tracking-wider">{p.tag}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section data-testid="platforms-cta-section" style={{ background: 'linear-gradient(135deg, #091F2C, #194449)' }} className="py-16">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-medium text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Ready to Explore Our Platforms?</h2>
          <p className="mb-7" style={{ color: 'rgba(255,255,255,0.65)' }}>Whether you want to learn, host an event, or practice in labs — we have a platform built for your needs.</p>
          <Link to="/contact" data-testid="platforms-contact-cta"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-semibold transition-colors"
            style={{ backgroundColor: '#D4EC8E', color: '#091F2C' }}
            onMouseEnter={e => e.currentTarget.style.backgroundColor = '#BFD872'}
            onMouseLeave={e => e.currentTarget.style.backgroundColor = '#D4EC8E'}>
            Contact Our Team <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
